import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  getAdminCategories,
  getAdminSubcategories,
  saveCategory,
  saveSubcategory,
  deleteCategory,
  deleteSubcategory,
  type CategoryPayload,
} from "@/services/adminService";
import type { AdminCategory } from "@/types/admin";
import { useAdminResource } from "@/pages/portal/admin/hooks/useAdminResource";
import AdminDataError from "@/pages/portal/admin/components/AdminDataError";
import AdminSearchInput from "@/pages/portal/admin/components/AdminSearchInput";
import AdminModal from "@/pages/portal/admin/components/AdminModal";
import {
  adminCard,
  adminTh,
  adminTd,
  adminBtnPrimary,
  adminBtnGhost,
  adminIconBtn,
  adminInput,
  adminLabel,
} from "@/pages/portal/admin/config/ui";

interface TaxonomySectionProps {
  kind: "category" | "subcategory";
}

const EMPTY: CategoryPayload = {
  name: "", slug: "", icon: "ri-apps-2-line", description: "", parentId: null, sortOrder: 1, isActive: true,
};

function slugify(value: string): string {
  return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function TaxonomySection({ kind }: TaxonomySectionProps) {
  const { accessToken } = useAuth();
  const isSub = kind === "subcategory";

  const { data, setData, loading, error, reload } = useAdminResource(
    () => (isSub ? getAdminSubcategories(accessToken) : getAdminCategories(accessToken)),
    [] as AdminCategory[],
    [accessToken, kind],
  );

  const [parents, setParents]     = useState<AdminCategory[]>([]);
  const [search, setSearch]       = useState("");
  const [busyId, setBusyId]       = useState<number | null>(null);
  const [editing, setEditing]     = useState<CategoryPayload | null>(null);
  const [editId, setEditId]       = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminCategory | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((c) => [c.name, c.slug, c.parentName ?? ""].join(" ").toLowerCase().includes(q));
  }, [data, search]);

  async function openCreate() {
    if (isSub) {
      try {
        setParents(await getAdminCategories(accessToken));
      } catch {
        setParents([]);
      }
    }
    setEditing({ ...EMPTY, parentId: isSub ? 1 : null, sortOrder: data.length + 1 });
    setEditId(null);
  }

  function openEdit(row: AdminCategory) {
    if (isSub) getAdminCategories(accessToken).then(setParents).catch(() => setParents([]));
    setEditing({
      name: row.name, slug: row.slug, icon: row.icon, description: row.description,
      parentId: row.parentId, sortOrder: row.sortOrder, isActive: row.isActive,
    });
    setEditId(row.id);
  }

  function setField<K extends keyof CategoryPayload>(key: K, value: CategoryPayload[K]) {
    setEditing((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  async function submit() {
    if (!editing?.name?.trim()) return;
    const payload: CategoryPayload = { ...editing, slug: editing.slug?.trim() || slugify(editing.name) };
    setBusyId(editId ?? -1);
    try {
      if (isSub) await saveSubcategory(accessToken, payload, editId ?? undefined);
      else       await saveCategory(accessToken, payload, editId ?? undefined);
      if (editId) {
        setData((prev) => prev.map((c) => (c.id === editId ? { ...c, ...payload, slug: payload.slug as string } : c)));
      } else {
        const parentName = isSub ? parents.find((p) => p.id === payload.parentId)?.name ?? null : null;
        setData((prev) => [
          ...prev,
          {
            id: Date.now(), name: payload.name as string, slug: payload.slug as string,
            icon: payload.icon ?? EMPTY.icon ?? "", description: payload.description ?? "",
            parentId: payload.parentId ?? null, parentName, businessCount: 0,
            sortOrder: payload.sortOrder ?? prev.length + 1, isActive: payload.isActive ?? true,
          },
        ]);
      }
      setEditing(null);
    } finally {
      setBusyId(null);
    }
  }

  async function toggleActive(row: AdminCategory) {
    const next = !row.isActive;
    setData((prev) => prev.map((c) => (c.id === row.id ? { ...c, isActive: next } : c)));
    if (isSub) await saveSubcategory(accessToken, { isActive: next, parentId: row.parentId }, row.id);
    else       await saveCategory(accessToken, { isActive: next }, row.id);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      if (isSub) await deleteSubcategory(accessToken, deleteTarget.id);
      else       await deleteCategory(accessToken, deleteTarget.id);
      setData((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    } finally {
      setBusyId(null);
    }
  }

  const label = isSub ? "subcategory" : "category";

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <AdminSearchInput value={search} onChange={setSearch} placeholder={`Search ${label}s…`} />
        <button type="button" onClick={openCreate} className={adminBtnPrimary}>
          <i className="ri-add-line text-sm" /> New {label}
        </button>
        <span className="ml-auto text-xs text-foreground-500">
          {loading ? "Loading…" : `${data.length} ${label}s`}
        </span>
      </div>

      {error && <AdminDataError message={error} onRetry={reload} />}

      <div className={adminCard}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
              <i className="ri-apps-2-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-medium text-foreground-700">No {label}s found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse">
              <thead className="border-b border-background-200">
                <tr>
                  <th className={adminTh}>{isSub ? "Subcategory" : "Category"}</th>
                  {isSub && <th className={adminTh}>Parent category</th>}
                  <th className={adminTh}>Slug</th>
                  <th className={adminTh}>Listings</th>
                  <th className={adminTh}>Order</th>
                  <th className={adminTh}>Visible</th>
                  <th className={`${adminTh} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {filtered.map((c) => (
                  <tr key={c.id} className="hover:bg-background-100/40">
                    <td className={adminTd}>
                      <div className="flex items-center gap-2.5">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                          <i className={`${c.icon || "ri-apps-2-line"} text-base`} />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-foreground-900">{c.name}</p>
                          {c.description && <p className="truncate text-[11px] text-foreground-400">{c.description}</p>}
                        </div>
                      </div>
                    </td>
                    {isSub && <td className={adminTd}>{c.parentName ?? "—"}</td>}
                    <td className={adminTd}><code className="rounded bg-background-100 px-1.5 py-0.5 text-[11px] text-foreground-600">{c.slug}</code></td>
                    <td className={`${adminTd} tabular-nums`}>{c.businessCount.toLocaleString("en-IN")}</td>
                    <td className={`${adminTd} tabular-nums`}>{c.sortOrder}</td>
                    <td className={adminTd}>
                      <button
                        type="button"
                        onClick={() => toggleActive(c)}
                        className={`inline-flex cursor-pointer items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold transition-colors ${
                          c.isActive ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-background-200 text-foreground-600 hover:bg-background-300"
                        }`}
                      >
                        {c.isActive ? "Visible" : "Hidden"}
                      </button>
                    </td>
                    <td className={`${adminTd} text-right`}>
                      <div className="flex items-center justify-end gap-1">
                        <button type="button" onClick={() => openEdit(c)} className={adminIconBtn} title={`Edit ${label}`}>
                          <i className="ri-pencil-line text-base" />
                        </button>
                        <button type="button" onClick={() => setDeleteTarget(c)} className={adminIconBtn} title={`Delete ${label}`}>
                          <i className="ri-delete-bin-line text-base text-rose-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <AdminModal
          title={editId ? `Edit ${label}` : `New ${label}`}
          onClose={() => setEditing(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setEditing(null)} className={adminBtnGhost}>Cancel</button>
              <button type="button" disabled={!editing.name?.trim() || busyId !== null} onClick={submit} className={adminBtnPrimary}>
                {editId ? "Save changes" : `Create ${label}`}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            {isSub && (
              <div>
                <label className={adminLabel} htmlFor="tx-parent">Parent category</label>
                <select id="tx-parent" value={editing.parentId ?? ""} onChange={(e) => setField("parentId", Number(e.target.value))} className={`${adminInput} cursor-pointer`}>
                  <option value="">Select parent…</option>
                  {parents.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className={adminLabel} htmlFor="tx-name">Name</label>
              <input id="tx-name" type="text" value={editing.name ?? ""} onChange={(e) => setField("name", e.target.value)} className={adminInput} />
            </div>
            <div>
              <label className={adminLabel} htmlFor="tx-slug">Slug</label>
              <input id="tx-slug" type="text" value={editing.slug ?? ""} onChange={(e) => setField("slug", e.target.value)} placeholder="auto-generated from name" className={adminInput} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={adminLabel} htmlFor="tx-icon">Icon class</label>
                <input id="tx-icon" type="text" value={editing.icon ?? ""} onChange={(e) => setField("icon", e.target.value)} placeholder="ri-store-line" className={adminInput} />
              </div>
              <div>
                <label className={adminLabel} htmlFor="tx-order">Sort order</label>
                <input id="tx-order" type="number" min={1} value={editing.sortOrder ?? 1} onChange={(e) => setField("sortOrder", Number(e.target.value) || 1)} className={adminInput} />
              </div>
            </div>
            <div>
              <label className={adminLabel} htmlFor="tx-desc">Description</label>
              <textarea id="tx-desc" rows={2} value={editing.description ?? ""} onChange={(e) => setField("description", e.target.value)} className={`${adminInput} h-auto py-2.5`} />
            </div>
            <label className="flex cursor-pointer items-center gap-2.5 text-sm text-foreground-700">
              <input type="checkbox" checked={!!editing.isActive} onChange={(e) => setField("isActive", e.target.checked)} className="h-4 w-4 cursor-pointer accent-primary-500" />
              Visible on the website
            </label>
          </div>
        </AdminModal>
      )}

      {deleteTarget && (
        <AdminModal
          title={`Delete ${label}`}
          subtitle={deleteTarget.name}
          onClose={() => setDeleteTarget(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setDeleteTarget(null)} className={adminBtnGhost}>Cancel</button>
              <button type="button" disabled={busyId === deleteTarget.id} onClick={confirmDelete} className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100">
                <i className="ri-delete-bin-line text-sm" /> Delete
              </button>
            </div>
          }
        >
          <p className="text-sm text-foreground-700">
            Delete <strong>{deleteTarget.name}</strong>? Listings assigned to it must be reassigned first.
          </p>
        </AdminModal>
      )}
    </div>
  );
}